/* eslint-disable @typescript-eslint/no-non-null-assertion */
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import {
  LearnerPurchaseRequest,
  useGetTeam,
  usePurchaseProfilesForTeam,
  useDeleteTeam,
  useGetPaginatedTeamLearners,
  useGetAvailableLearnersForTeam,
  TEAM_LEARNERS_QUERY_SCOPES,
} from 'api';
import {
  AddRemoveModal,
  TextV2,
  Counter,
  Alert,
} from '@insights-ltd/design-library/components';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router';
import Breadcrumbs from 'components/Breadcrumbs';
import {
  FullScreenError as PageError,
  FullScreenSpinner as PageSpinner,
} from 'components/FullScreen';
import Notification from 'components/Notification';
import useClearHistoryState from 'components/hooks/useClearHistoryState';
import { Button, styled } from '@mui/material';
import PlusIcon from '@mui/icons-material/Add';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { EvaluatorSearchFilter } from 'api/evaluatorLinks/evaluatorLinksApiTypes';
import PurchasePage from 'components/PurchasePage/PurchasePage';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useQueryClient } from '@tanstack/react-query';
import Helmet from 'react-helmet';
import {
  isMinLengthString,
  MINIMUM_SEARCH_CHARACTER_LIMIT_CONTAINS,
  useSearch,
} from 'api/search';
import { SearchFilter } from 'components/SearchFilter/SearchFilter';
import { TeamHeader, useAddRemoveModal } from './Team.helper';
import Actions from './Actions';
import LearnersList from './LearnersList/LearnersList';
import { TransformedLearnerResponse } from './LearnersList/learnerType';
import FullHeight from '../../components/FullHeight';
import LearnerSelectionBar from '../CompletedEvaluators/LearnerSelectionBar/LearnerSelectionBar';

type TeamProps = {
  createSuccess?: boolean;
};

const StyledSuccessMsgBox = styled('div')(({ theme }) => ({
  padding: `${theme.spacing(spacingSizeMap.XS)} ${theme.spacing(
    spacingSizeMap.XS,
  )} ${theme.spacing(spacingSizeMap.XS)} ${theme.spacing(spacingSizeMap.S)}`,
  background: theme.palette.primary.light,
  borderRadius: '4px',
  maxWidth: '720px',
}));

const StyledSuccessMsg = styled(TextV2)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
}));

const minSearchCharacters = MINIMUM_SEARCH_CHARACTER_LIMIT_CONTAINS;

const toTeamLearnerSearchTerm = (searchTerm: string) =>
  isMinLengthString(searchTerm, minSearchCharacters) ? searchTerm : '';

const Team = ({ createSuccess = false }: TeamProps) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { state, pathname } = useLocation();
  const removed = state?.remove;
  useClearHistoryState(removed);
  const { teamId } = useParams<{ teamId: string }>();

  const [checkedItems, setCheckedItems] = useState<{
    [id: string]: TransformedLearnerResponse;
  }>({});
  const isPurchaseRoute = pathname.includes('purchase-profiles');
  const parentLink = `/teams/${teamId}`;

  const removeCheckedItems = (removeItems: string[]) => {
    setCheckedItems((prev) => {
      const checkedItemsMap = { ...prev };
      removeItems.forEach((id) => {
        delete checkedItemsMap[id];
      });

      if (Object.keys(checkedItemsMap).length === 0 && isPurchaseRoute) {
        navigate(parentLink);
      }

      return checkedItemsMap;
    });
  };
  const { setSearchString, searchString, searchTerm } =
    useSearch(minSearchCharacters);
  const {
    setSearchString: setRemoveLearnerSearchString,
    searchTerm: removeLearnerSearchTerm,
    isBelowMinimum: isRemoveLearnerSearchBelowMinimum,
    isDebouncing: isRemoveLearnerSearchDebouncing,
  } = useSearch(minSearchCharacters);
  const [filter, setFilter] = useState<EvaluatorSearchFilter>('ALL');
  const [availableLearnerSearchTerm, setAvailableLearnerSearchTerm] =
    useState<string>('');

  const searchFilterRef = useRef<HTMLInputElement>(null);

  const resetSearchCriteria = async () => {
    setSearchString('');
    setFilter('ALL');
  };

  const {
    mutate,
    error,
    isError: isPurchaseError,
    isPending: isPurchaseLoading,
    reset: resetMutate,
  } = usePurchaseProfilesForTeam(queryClient);

  const {
    isPending: isDeleting,
    reset: resetDelete,
    mutate: deleteTeam,
  } = useDeleteTeam(queryClient);
  const {
    isPending,
    isError,
    data: team,
    refetch,
  } = useGetTeam(teamId || '', { enabled: !isDeleting });

  const filterOptions: { label: string; value: EvaluatorSearchFilter }[] = [
    {
      label: t(
        'ui.event-management.evaluator-links.completed-evaluators.search-filter.label.all',
      ),
      value: 'ALL',
    },
    {
      label: t(
        'ui.event-management.evaluator-links.completed-evaluators.search-filter.label.purchased',
      ),
      value: 'PURCHASED',
    },
    {
      label: t(
        'ui.event-management.evaluator-links.completed-evaluators.search-filter.label.unpurchased',
      ),
      value: 'UNPURCHASED',
    },
  ];
  const filterProps = {
    options: filterOptions,
    value: filter,
    onChange: (value: EvaluatorSearchFilter) => {
      setFilter(value);
    },
  };

  const {
    data: availableLearnersData,
    isFetching: isFetchingAvailableLearners,
  } = useGetAvailableLearnersForTeam(teamId ?? '', availableLearnerSearchTerm);

  const availableLearners = availableLearnersData?.learners ?? [];
  const availableLearnersLimitExceeded =
    availableLearnersData?.limitExceeded ?? false;

  const { learnerCount: teamLearnerCount = 0 } = team || {
    organisation: { learnerCount: 0 },
  };

  const availableLearnerCount = team?.organisationContext?.distinctLearnerCount
    ? team.organisationContext.distinctLearnerCount - teamLearnerCount
    : 0;

  const {
    onAdd,
    onRemove,
    availableItems,
    onClose,
    isOpen,
    toggleAddRemoveModal,
    translations,
    onAddAll,
    onRemoveAll,
    isAddLoading,
    isMutating,
  } = useAddRemoveModal(
    team,
    availableLearners,
    refetch,
    availableLearnerSearchTerm,
    isRemoveLearnerSearchBelowMinimum,
    availableLearnerCount,
    availableLearnersLimitExceeded,
    checkedItems,
    removeCheckedItems,
  );

  const {
    isFetching,
    status,
    hasMore: hasMoreToLoad,
    paginator,
    loadMore,
  } = useGetPaginatedTeamLearners(
    {
      teamId: teamId ?? '',
      searchTerm: toTeamLearnerSearchTerm(searchTerm),
      purchaseStatus: filter,
    },
    {
      enabled: !!teamId && !isOpen,
      queryKeyScope: TEAM_LEARNERS_QUERY_SCOPES.TABLE,
    },
  );

  const isLearnerError = status === 'error';
  const isLoading = status === 'pending';

  const {
    unprocessedTeamLearners: groupedTeamLearners,
    hasMore: hasMoreGroupedItems,
    loadMore: onGroupedItemsLoadMore,
    isFetching: isFetchingGroupedItems,
    isPlaceholderData: isShowingPreviousGroupedItems,
    totalCount: groupedItemsTotalCount,
  } = useGetPaginatedTeamLearners(
    {
      teamId: teamId ?? '',
      searchTerm: toTeamLearnerSearchTerm(removeLearnerSearchTerm),
      purchaseStatus: 'ALL',
    },
    {
      enabled: !!teamId && isOpen,
      queryKeyScope: TEAM_LEARNERS_QUERY_SCOPES.REMOVE_LEARNER_MODAL,
      keepPreviousResults: true,
    },
  );
  const isAwaitingSearchResults =
    isRemoveLearnerSearchDebouncing && removeLearnerSearchTerm === '';

  const showGroupedItems =
    !isRemoveLearnerSearchBelowMinimum && !isAwaitingSearchResults;
  const isLoadingGroupedItems =
    !isRemoveLearnerSearchBelowMinimum &&
    (isRemoveLearnerSearchDebouncing || isFetchingGroupedItems);

  const evaluationIds = Object.keys(checkedItems);
  const title = searchTerm
    ? t('ui.event-management.learners.teams.learner-results')
    : t('ui.event-management.learners.teams.learners');
  const pageTitle = t(
    'ui.event-management.learners.purchase-profiles.page-title',
  );

  const { learnerName, teamName } = removed ?? {};
  const notification = removed
    ? t('ui.event-management.learners.teams.delete-learner-success', {
        learnerName,
        teamName,
      })
    : null;

  const handlePurchase = (
    requestData: LearnerPurchaseRequest,
    onSuccess: () => void,
    onFailure: () => void,
  ) =>
    mutate(
      {
        teamId: teamId!,
        request: requestData,
      },
      {
        onSuccess: () => {
          onSuccess();
          removeCheckedItems(evaluationIds);
          navigate(`/teams/${teamId}`);
        },
        onError: () => {
          onFailure();
        },
      },
    );

  const apiPaginationProps = {
    isFetching,
    hasMoreToLoad,
    loadMore,
    loadingState: status,
  };

  const handleConfirmDelete = (onError: () => void, onSettled: () => void) =>
    deleteTeam(team!.id, {
      onSuccess: () =>
        navigate('/learners', {
          state: {
            activeTab: 'teams',
            deletedTeam: team!.name,
          },
        }),
      onError,
      onSettled,
    });

  if (isPending) {
    return <PageSpinner message={t('ui.event-management.loading')} />;
  }

  if (isError || isLearnerError) {
    return (
      <PageError
        message={t('ui.event-management.learners.error-loading-teams')}
      />
    );
  }

  const teamLinkCrumb: Record<string, string> = {
    '/': t('ui.event-management.events.nav.home'),
    '/Learners & teams': t('ui.event-management.events.nav.learners-and-teams'),
    [parentLink]: team.name,
  };

  const learnerIds = Object.keys(checkedItems).map(
    (key) => checkedItems[key].learnerId,
  );
  const selectedLearnerOrgIds = Array.from(
    new Set(
      Object.values(checkedItems).flatMap(({ latestEvaluation }) =>
        latestEvaluation?.organisation?.id
          ? [latestEvaluation.organisation.id]
          : [],
      ),
    ),
  );
  return (
    <>
      <Helmet>
        <title>{team.name}</title>
      </Helmet>
      <FullHeight
        sx={{
          backgroundColor: isPurchaseRoute ? 'white' : 'white | grey',
          paddingRight: '0 !important',
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: 'calc(100% - 100px)',
            paddingRight: '0 !important',
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              marginBottom: !evaluationIds.length ? '0 !important' : '4rem',
              paddingLeft: '0 !important',
              paddingRight: '0 !important',
              height: '100%',
            }}
          >
            <PurchasePage
              onPurchase={handlePurchase}
              title={pageTitle}
              indexRoute={
                <>
                  <Container
                    maxWidth={false}
                    sx={(theme) => ({ background: theme.palette.blue.light })}
                  >
                    <Container maxWidth="lg">
                      <Box
                        sx={{
                          py: (theme) => theme.spacing(spacingSizeMap.M),
                        }}
                      >
                        <Breadcrumbs
                          crumbs={{
                            '/': t('ui.event-management.dashboard.home.title'),
                            '/learners': t(
                              'ui.event-management.learners-and-teams.title',
                            ),
                          }}
                          activeText={team!.name}
                        />
                      </Box>
                      <Box
                        sx={{
                          pb: (theme) => theme.spacing(spacingSizeMap.XS),
                        }}
                      />
                      <Link
                        component={RouterLink}
                        to="/learners"
                        state={{ activeTab: 'teams' }}
                      >
                        <Box
                          sx={{
                            alignItems: 'center',
                            display: 'flex',
                            mb: '.5rem',
                            ml: '-8px',
                          }}
                        >
                          <ChevronLeftIcon />
                          <TextV2
                            variant="bodyBold"
                            component="p"
                            color="primary"
                          >
                            {t('ui.event-management.learners.back-to-teams')}
                          </TextV2>
                        </Box>
                      </Link>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Grid size={{ md: 7 }}>
                          <TeamHeader team={team} />
                        </Grid>
                        <Actions
                          team={team}
                          isDeleting={isDeleting}
                          onDelete={handleConfirmDelete}
                          reset={resetDelete}
                        />
                      </Box>
                      <Box
                        sx={{
                          pb: (theme) => theme.spacing(spacingSizeMap.L),
                        }}
                      />
                      <Box
                        sx={{
                          pb: (theme) => theme.spacing(spacingSizeMap.L),
                        }}
                      />
                    </Container>
                  </Container>
                  <Container maxWidth="lg">
                    <Box
                      sx={{
                        mt: -7,
                      }}
                    >
                      <SearchFilter<{
                        rightSelectOptionsType: EvaluatorSearchFilter;
                      }>
                        labelText={`${t(
                          'ui.event-management.list-filter.search',
                        )} ${t('ui.event-management.list-filter.by-name')}`}
                        placeholder={t(
                          'ui.event-management.learners.search.placeholder',
                        )}
                        inputState={searchString}
                        handleTermChange={setSearchString}
                        showButton={false}
                        ref={searchFilterRef}
                        minCharacters={minSearchCharacters}
                        rightSelectProps={filterProps}
                      />
                    </Box>
                  </Container>
                  {team.migrated && (
                    <Container maxWidth="lg">
                      <Box
                        sx={{
                          mt: 1,
                          mb: 5,
                          maxWidth: '61%',
                        }}
                      >
                        <Alert severity="warning">
                          {t('ui.event-management.team.migrated-teams.warning')}
                        </Alert>
                      </Box>
                    </Container>
                  )}
                  {createSuccess && (
                    <Container maxWidth="lg">
                      <Grid size={{ xs: 12, md: 8 }}>
                        <StyledSuccessMsgBox>
                          <StyledSuccessMsg>
                            {t(
                              'ui.event-management.learners.teams.create-team-success',
                              {
                                teamName: team!.name,
                              },
                            )}
                          </StyledSuccessMsg>
                        </StyledSuccessMsgBox>
                      </Grid>
                    </Container>
                  )}
                  <Container maxWidth="lg">
                    <Box
                      sx={{
                        mt: 2,
                        mb: 0,
                      }}
                    >
                      {notification && <Notification message={notification} />}
                      <Grid
                        container
                        spacing={2}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Grid size={{ xs: 12, sm: 'auto' }}>
                          {!isLoading && (
                            <TextV2 variant="h3">
                              {title}
                              &nbsp;
                              <Counter
                                count={paginator.totalCount}
                                size="large"
                              />
                            </TextV2>
                          )}
                        </Grid>
                        {!team.migrated && (
                          <Grid>
                            <Button
                              data-testid="add-remove-learners"
                              variant="contained"
                              disabled={false}
                              startIcon={<PlusIcon />}
                              onClick={() => {
                                resetSearchCriteria();
                                toggleAddRemoveModal();
                              }}
                            >
                              {t(
                                'ui.event-management.team.add-remove-modal.toggle-modal',
                              )}
                            </Button>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                    {!isLoading ? (
                      <LearnersList
                        evaluationIds={evaluationIds}
                        teamId={teamId!}
                        checkedItems={checkedItems}
                        setCheckedItems={setCheckedItems}
                        removeCheckedItems={removeCheckedItems}
                        migrated={!!team.migrated}
                        type={team.organisationContext?.type}
                        selectable={!team.migrated}
                        paginator={paginator}
                        apiPaginationProps={apiPaginationProps}
                      />
                    ) : (
                      <PageSpinner />
                    )}
                    <AddRemoveModal
                      onRemove={onRemove}
                      onAdd={onAdd}
                      onClose={onClose}
                      onAddAll={onAddAll}
                      onRemoveAll={onRemoveAll}
                      isAddLoading={isAddLoading}
                      availableItems={availableItems!}
                      itemsInGroup={showGroupedItems ? groupedTeamLearners : []}
                      isOpen={isOpen}
                      filterBy={['fullName', 'primaryEmail']}
                      onSearchTermChange={setAvailableLearnerSearchTerm}
                      clearSearch={() => setAvailableLearnerSearchTerm('')}
                      onGroupedItemsSearchTermChange={
                        setRemoveLearnerSearchString
                      }
                      clearGroupedItemsSearch={() =>
                        setRemoveLearnerSearchString('')
                      }
                      hasMoreGroupedItems={
                        showGroupedItems && hasMoreGroupedItems
                      }
                      onGroupedItemsLoadMore={onGroupedItemsLoadMore}
                      isLoadingMoreGroupedItems={isLoadingGroupedItems}
                      groupedItemsTotalCount={
                        showGroupedItems && !isShowingPreviousGroupedItems
                          ? groupedItemsTotalCount
                          : 0
                      }
                      translations={translations}
                      showAddAll={!!availableLearnerCount}
                      showRemoveAll={!!team.learnerCount}
                      showError={!!translations.errorMsg}
                      isMutating={
                        isMutating ||
                        isFetchingGroupedItems ||
                        isFetchingAvailableLearners
                      }
                    />
                  </Container>
                </>
              }
              breadcrumbs={teamLinkCrumb}
              organisationIds={selectedLearnerOrgIds}
              removeCheckedItems={removeCheckedItems}
              parentLink={parentLink}
              evaluationIds={evaluationIds}
              onReset={resetMutate}
              isError={isPurchaseError}
              isPurchasing={isPurchaseLoading}
              error={error}
              type="Teams"
            />
          </Container>
          {evaluationIds.length > 0 && !isPurchaseRoute ? (
            <LearnerSelectionBar
              evaluationIds={evaluationIds}
              learnerIds={learnerIds}
              learners={checkedItems}
              teamId={teamId}
              setCheckedItems={setCheckedItems}
            />
          ) : null}
        </Box>
      </FullHeight>
    </>
  );
};

export default Team;
/* eslint-enable @typescript-eslint/no-non-null-assertion */
